import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography } from "@mui/material";
import MuiInput from "@mui/material/FilledInput";
import { Slider } from "@mui/material";
import { styled } from "@mui/material/styles";
import SliderTitle from "./sliderTitle";

const Input = styled(MuiInput)(({ theme }) => ({
  backgroundColor: "#f3f7f9",
  fontWeight: 700,
  color: "#878787",
  fontSize: "1.25rem",
  "& .MuiInputBase-input": {
    textAlign: "center",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100px",
    height: "30px",
    fontSize: "1.25rem",
  },
  [theme.breakpoints.up("sm")]: {
    width: "100px",
    height: "50px",
  },
}));

function SliderRow({
  line1 = "",
  line2 = "",
  min = 0,
  max = 100,
  value,
  setValue,
}) {
  const handleSliderChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleInputChange = (event) => {
    setValue(event.target.value === "" ? "" : Number(event.target.value));
  };

  const handleBlur = () => {
    if (value < min) {
      setValue(min);
    } else if (value > max) {
      setValue(max);
    }
  };

  return (
    <Grid container spacing="1rem" alignItems="center">
      <Grid item xs={12} sm={4}>
        <Typography gutterBottom variant="body2" component="div" align="left">
          {line1}
        </Typography>
        <Typography gutterBottom variant="body2" component="div" align="left">
          {line2}
        </Typography>
      </Grid>
      <Grid item xs sm>
        <Slider
          min={min}
          max={max}
          value={typeof value === "number" ? value : 0}
          onChange={handleSliderChange}
          aria-labelledby="input-slider"
          sx={{
            height: "15px",
            "& .MuiSlider-thumb": {
              width: "33px",
              height: "33px",
              backgroundColor: "white",
              boxShadow: "0px 0px 4px 0px rgba(66, 68, 90, 1)",
            },
            "& .MuiSlider-rail": {
              opacity: 1,
              color: "#f3f7f9",
            },
          }}
        ></Slider>
      </Grid>
      <Grid item xs="auto">
        <Input
          value={value}
          size="small"
          hiddenLabel={true}
          disableUnderline={true}
          onChange={handleInputChange}
          onBlur={handleBlur}
          inputProps={{
            step: 1,
            min: min,
            max: max,
            type: "number",
            "aria-labelledby": "input-slider",
          }}
        ></Input>
      </Grid>
    </Grid>
  );
}

function Separator() {
  return (
    <div
      style={{
        borderBottom: "1px solid #e5e5e5",
        marginBottom: "1rem",
        paddingBottom: "1rem",
      }}
    />
  );
}

const Container = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: "2rem 1rem",
    flex: 1,
  },
  [theme.breakpoints.up("sm")]: {
    padding: "5rem 2rem 4rem 3rem",
    flex: 1,
  },
}));

function SliderPanel({ sliders, currency, surfaceUnit }) {
  const { t } = useTranslation(["common"]);

  useEffect(() => {
    sliders.setAreaSize(Math.min(sliders.areaSize, surfaceUnit.maxSurface));
  }, [surfaceUnit, sliders]);

  return (
    <Container>
      <SliderTitle className="slider-title" />
      <SliderRow
        line1={t("Size of the area cleaned:")}
        line2={
          <span>{t("(Area in m2)", { surfaceUnit: surfaceUnit?.label })}</span>
        }
        max={surfaceUnit.maxSurface}
        value={sliders.areaSize}
        setValue={sliders.setAreaSize}
      />
      <Separator />
      <SliderRow
        line1={t("Cleaned per week:")}
        line2={t("(Number of times)")}
        max={35}
        value={sliders.cleanedPerWeek}
        setValue={sliders.setCleanedPerWeek}
      />
      <Separator />
      <SliderRow
        line1={t("Labor cost:")}
        line2={t("($$ per hour)", { currency: currency?.symbol })}
        max={sliders.maxCost}
        value={sliders.labourCost}
        setValue={sliders.setLabourCost}
      />
      <Separator />
      <SliderRow
        line1={t("Detergent dilution rate:")}
        line2={t("(Ratio of water to detergent)")}
        min={0}
        max={750}
        value={sliders.chemicalDilutionRate}
        setValue={sliders.setChemicalDilutionRate}
      />
      <Separator />
      <SliderRow
        line1={t("Cost of detergents:")}
        line2={t("($$ per liter)", { currency: currency?.symbol })}
        max={sliders.maxCost}
        value={sliders.costOfChemicals}
        setValue={sliders.setCostOfChemicals}
      />
    </Container>
  );
}

export default SliderPanel;
